import type { ErrorLike } from '@apollo/client';

import {
  AddPaymentMethodErrorReasons,
  ApiErrorCodes,
} from '@constants/apiErrors';
import { ValidationErrorMessages } from '@constants/forms';
import {
  EMAIL_EXISTS_ERROR,
  INVALID_BANK_ACCOUNT_DETAILS_ERROR,
  INVALID_BANK_ACCOUNT_ERROR,
  INVALID_SORT_CODE_ERROR,
} from '@features/details/constants';
import { containsErrorCode } from '@utils/apiErrors';

interface HandleSubmitErrorsArgs {
  setFieldError: (fieldName: string, message: string) => void;
  setSubmitErrors: (errors: ErrorLike[]) => void;
  submitResponseErrors: ErrorLike[];
}

interface FieldErrors {
  aboutEmail: boolean;
  sortCode: boolean;
  account: boolean;
}

const getErrorMessage = (error: ErrorLike): string => {
  const message = error.message;

  if (
    message.includes(AddPaymentMethodErrorReasons.invalidSortCode) ||
    message.includes(AddPaymentMethodErrorReasons.missingSortCode)
  ) {
    return INVALID_SORT_CODE_ERROR;
  }

  if (
    message.includes(AddPaymentMethodErrorReasons.invalidUnspecified) ||
    message.includes(AddPaymentMethodErrorReasons.invalidModulusCheck) ||
    message.includes(AddPaymentMethodErrorReasons.invalidRollNumber)
  ) {
    return INVALID_BANK_ACCOUNT_DETAILS_ERROR;
  }

  if (message.includes(AddPaymentMethodErrorReasons.invalidAccountNumber)) {
    return INVALID_BANK_ACCOUNT_ERROR;
  }

  if (
    message.includes(ApiErrorCodes.emailAlreadyRegistered) ||
    containsErrorCode(ApiErrorCodes.emailAlreadyRegistered, error)
  ) {
    return EMAIL_EXISTS_ERROR;
  }

  return message;
};

const updateFieldErrors = (error: ErrorLike, fieldErrors: FieldErrors) => {
  switch (error.message) {
    case INVALID_SORT_CODE_ERROR:
      fieldErrors.sortCode = true;
      break;
    case INVALID_BANK_ACCOUNT_DETAILS_ERROR:
      fieldErrors.sortCode = true;
      fieldErrors.account = true;
      break;
    case INVALID_BANK_ACCOUNT_ERROR:
      fieldErrors.account = true;
      break;
    case EMAIL_EXISTS_ERROR:
      fieldErrors.aboutEmail = true;
      break;
  }
};

const handleFieldErrors = (
  fieldErrors: FieldErrors,
  setFieldError: (field: string, message: string) => void
) => {
  if (fieldErrors.aboutEmail) {
    setFieldError('aboutEmail', ValidationErrorMessages.EMAIL_EXISTS);
  }

  if (fieldErrors.sortCode) {
    setFieldError('ddSortCode', ValidationErrorMessages.INVALID_SORT_CODE);
  }

  if (fieldErrors.account) {
    setFieldError(
      'ddAccountNumber',
      ValidationErrorMessages.INVALID_ACCOUNT_NUMBER
    );
  }
};

const handleSubmitErrors = ({
  submitResponseErrors,
  setFieldError,
  setSubmitErrors,
}: HandleSubmitErrorsArgs) => {
  const fieldErrors: FieldErrors = {
    aboutEmail: false,
    sortCode: false,
    account: false,
  };

  const errors = submitResponseErrors.reduce((apolloErrors, error) => {
    if (!error) {
      return apolloErrors;
    }

    const message = getErrorMessage(error);
    error.message = message;

    updateFieldErrors(error, fieldErrors);

    apolloErrors.push(error);
    return apolloErrors;
  }, [] as ErrorLike[]);

  const hasErrors = errors.length > 0;

  if (hasErrors) {
    handleFieldErrors(fieldErrors, setFieldError);
    setSubmitErrors(errors);
  }

  return hasErrors;
};

export { handleSubmitErrors, handleFieldErrors, getErrorMessage };
